import { Box, Flex, useToast } from '@lego/klik-ui';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@/components/common';
import { BreadCrumb } from '@/components/common/BreadCrumb';
import { ImageTagDetails } from '@/components/repository/ImageTagDetails';
import { IndexChildrenTable } from '@/components/repository/IndexChildrenTable';
import { ScanFindingsTable } from '@/components/repository/ScanFindingsTable';
import { useDocumentTitle } from '@/hooks/common';
import { useImageTagDetail } from '@/hooks/repository';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { paths } from '@/utility/global_paths';
import { ToastOptions } from '@/utility/ToastOptions';

const ImageTagDetailPageContent = ({
  workspaceId,
  repositoryId,
  imageTag,
}: {
  workspaceId: number;
  repositoryId: number;
  imageTag: string;
}) => {
  const { data, error, isLoading } = useImageTagDetail(workspaceId, repositoryId, imageTag);
  const dataReady = !isLoading && !error && data;
  const tagDetail = data;

  useRegisterPageContext(tagDetail);
  useDocumentTitle(tagDetail?.tag ? `Tag ${tagDetail.tag}` : 'Image Tag');

  return (
    <Box>
      {!dataReady ? (
        <Loader />
      ) : (
        <Box>
          <Box>
            <BreadCrumb
              crumbs={[
                { label: 'Workspaces', path: paths.workspaces },
                {
                  label: `Workspace #${workspaceId}`,
                  path: paths.workspaceById(workspaceId),
                },
                {
                  label: `Repository #${repositoryId}`,
                  path: paths.repositoryById(workspaceId, repositoryId),
                },
                {
                  label: `Tag ${tagDetail!.tag}`,
                  path: paths.imageTagById(workspaceId, repositoryId, tagDetail!.tag),
                },
              ]}
            />
          </Box>
          <Flex direction="column" gap={8} marginY={8}>
            <ImageTagDetails tagDetail={tagDetail!} workspaceId={workspaceId} />
            {tagDetail!.children && tagDetail!.children.length > 0 ? (
              <IndexChildrenTable
                applications={tagDetail!.applications}
                imageTag={imageTag}
                indexChildren={tagDetail!.children}
                repositoryId={repositoryId}
                workspaceId={workspaceId}
              />
            ) : (
              <ScanFindingsTable
                applications={tagDetail!.applications}
                imageTag={imageTag}
                repositoryId={repositoryId}
                scanResults={tagDetail!.scanResults}
                workspaceId={workspaceId}
              />
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export const ImageTagDetailPage = () => {
  const { workspaceId, repositoryId, imageTag } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const parsedWorkspaceId = workspaceId ? Number.parseInt(workspaceId, 10) : null;
  const parsedRepositoryId = repositoryId ? Number.parseInt(repositoryId, 10) : null;

  const invalidWorkspace = !workspaceId || !parsedWorkspaceId;
  const invalidRepository = !repositoryId || !parsedRepositoryId;
  const invalidTag = !imageTag;

  useEffect(() => {
    if (invalidWorkspace) {
      toast(ToastOptions.error('Invalid WorkspaceId', 'An invalid ID was specified'));
      navigate(paths.workspaces);
    } else if (invalidRepository) {
      toast(ToastOptions.error('Invalid RepositoryId', 'An invalid ID was specified'));
      navigate(paths.workspaceById(parsedWorkspaceId!));
    } else if (invalidTag) {
      toast(ToastOptions.error('Invalid Image Tag', 'An invalid tag was specified'));
      navigate(paths.repositoryById(parsedWorkspaceId!, parsedRepositoryId!));
    }
  }, [
    invalidWorkspace,
    invalidRepository,
    invalidTag,
    parsedWorkspaceId,
    parsedRepositoryId,
    navigate,
    toast,
  ]);

  if (invalidWorkspace || invalidRepository || invalidTag) {
    return null;
  }

  return (
    <ImageTagDetailPageContent
      imageTag={imageTag}
      repositoryId={parsedRepositoryId}
      workspaceId={parsedWorkspaceId}
    />
  );
};
